import { Injectable, signal, computed } from '@angular/core';
import { AppSection } from './shell-state.service';

/**
 * RESPONSIBILITY: Aggregates notification counts per app section.
 *
 * SIGNALS:
 *   sectionBadges — per-section badge state
 *   aggregateBadge — highest-urgency badge for the profile avatar's dot
 *
 * USED BY: ProfileAvatarComponent, ProfilePanelComponent (Phase 3)
 *
 * BACKEND CONTRACT: Consumes GraphQL subscription from rdx-platform-api InAppChannel.
 * Currently stubbed — real subscription will be connected in Phase 3 (notifications tab).
 */

export interface SectionBadge {
  section: AppSection;
  type: 'none' | 'dot' | 'count';
  colour: 'blue' | 'red';
  count?: number;
}

export interface AggregateBadge {
  type: 'none' | 'dot' | 'count';
  colour: 'blue' | 'red';
  count?: number;
}

@Injectable({ providedIn: 'root' })
export class BadgeService {
  private readonly _sectionBadges = signal<Map<AppSection, SectionBadge>>(new Map());

  readonly sectionBadges = this._sectionBadges.asReadonly();

  readonly aggregateBadge = computed<AggregateBadge>(() => {
    const badges = this._sectionBadges();
    let urgentCount = 0;
    let infoCount = 0;
    let hasUrgent = false;

    for (const badge of badges.values()) {
      if (badge.type === 'none') continue;
      if (badge.colour === 'red') {
        hasUrgent = true;
        urgentCount += badge.count ?? 1;
      } else {
        infoCount += badge.count ?? 1;
      }
    }

    if (hasUrgent) {
      return { type: 'count', colour: 'red', count: urgentCount };
    }
    if (infoCount > 0) {
      return { type: 'dot', colour: 'blue' };
    }
    return { type: 'none', colour: 'blue' };
  });

  /** Get badge for a specific section */
  getBadge(section: AppSection): SectionBadge | undefined {
    return this._sectionBadges().get(section);
  }

  /** Update badge for a section (called when notification data arrives) */
  updateBadge(badge: SectionBadge): void {
    this._sectionBadges.update(map => {
      const next = new Map(map);
      next.set(badge.section, badge);
      return next;
    });
  }

  /** Clear badge for a section */
  clearBadge(section: AppSection): void {
    this._sectionBadges.update(map => {
      const next = new Map(map);
      next.delete(section);
      return next;
    });
  }
}
